;globalThis["_sentryRouteManifest"] = "{\"isrRoutes\":[],\"dynamicRoutes\":[],\"staticRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.3.5";import * as Sentry from '@sentry/nextjs'

Sentry.init({
	enabled: process.env.NODE_ENV !== 'development',
	release: process.env.VERCEL_GITHUB_COMMIT_SHA,
	environment: process.env.NEXT_PUBLIC_FUNDS_ENV,
	dsn: process.env.SENTRY_DSN,
	tracesSampleRate: 0.1,
	sendDefaultPii: false,
	normalizeDepth: 5,
	ignoreErrors: [
		// Generic browser noise
		/^Script error\.?$/,
		/^ResizeObserver loop/,
		// Transient network errors — not actionable
		'Failed to fetch',
		'Load failed',
		'NetworkError',
		'Network request failed',
		'ChunkLoadError',
		// 401 login failures — expected user behaviour, not bugs
		'Login error',
		// Google Tag Manager / Analytics blocked by adblockers — outside our control
		/Failed to load.*googletagmanager\.com/,
		/Load failed.*googletagmanager\.com/,
		/^Failed to load https:\/\/(?:www\.)?google-analytics\.com/,
		// iOS-only native bridge invoked from regular browser context (webview probes)
		/window\.webkit\.messageHandlers/,
		// reCAPTCHA — 3rd party timeouts and missing config on pages that don't use it
		/Non-Error promise rejection captured with value: Timeout/,
		/reCAPTCHA Timeout/,
		// Browser extension messages leaking via runtime.sendMessage
		'Invalid call to runtime.sendMessage(). Tab not found.',
		// Next.js navigation aborts — user navigated away before route loaded
		'Cancel rendering route',
		/Abort fetching component for route/,
		/Route did not complete loading/,
		// Browser feature-detection extensions injecting bogus probes
		/^Error: feature named `\w+` was not found$/,
		// MS Office/Teams-style embedded-webview messaging noise
		/Object Not Found Matching Id/,
		// Facebook/Instagram in-app browser tearing down its WKWebView mid-navigation
		'The WKWebView was deallocated before the message was delivered',
	],
	denyUrls: [/chrome-extension:\/\//, /moz-extension:\/\//, /safari-extension:\/\//, /safari-web-extension:\/\//],
	beforeSend: (event) => {
		// Drop 401 auth failures — expected user behaviour, not bugs
		if (event?.contexts?.Error?.response?.status === 401) return null

		const message = event.exception?.values?.[0]?.value ?? event.message ?? ''
		const frames = event.exception?.values?.[0]?.stacktrace?.frames || []
		const ex = event.exception?.values?.[0]

		// Android WebView Java bridge lifecycle — OS GC's the Java object mid-use.
		if (
			message.includes('Java object is gone') ||
			message.includes('Java bridge method invocation error') ||
			message.includes('Error invoking postMessage: Java')
		) {
			return null
		}

		// Browser storage blocked by privacy settings (private mode / strict tracking protection).
		if (
			message.includes('The operation is insecure') ||
			(message.includes('localStorage') && message.includes('Access is denied'))
		) {
			return null
		}

		// Chrome-on-iOS WebKit-injected "Maximum call stack size exceeded" noise.
		// Four-signal guard preserves real app recursion bugs.
		if (
			ex?.type === 'RangeError' &&
			ex?.value?.includes('Maximum call stack size exceeded') &&
			ex?.mechanism?.type === 'onerror' &&
			!frames.some((f) => f.in_app)
		) {
			return null
		}

		// Samsung Internet / in-app WebView browsers inject globals (`xbrowser`,
		// `swbrowser`) for features that don't exist in this context.
		if (/\b(xbrowser|swbrowser)\b/.test(message)) {
			return null
		}

		// Same WebKit-injected-script family as the RangeError guard above, just a
		// different symptom: a parse error or an opaque 1-3 char error thrown from
		// a script with no app frames at all (not even an unresolved sourcemap).
		if (!frames.some((f) => f.in_app) && ex?.mechanism?.type === 'onerror') {
			if (ex?.type === 'SyntaxError') {
				return null
			}
			if (/^[A-Za-z]{1,3}$/.test(ex?.value || '')) {
				return null
			}
		}

		// Browser extensions that JSON.stringify() a DOM node still carrying a
		// React fiber reference — the circularity is in the extension's own code.
		if (message.includes('Converting circular structure to JSON') && !frames.some((f) => f.in_app)) {
			return null
		}

		// Never let a kennitala reach Sentry even indirectly (an interpolated
		// error message, a thrown validation error echoing user input, etc).
		const KENNITALA_REGEX = /\b\d{6}-?\d{4}\b/g
		if (event.message && KENNITALA_REGEX.test(event.message)) {
			event.message = event.message.replace(KENNITALA_REGEX, '[REDACTED]')
		}
		event.exception?.values?.forEach((value) => {
			if (value.value) {
				value.value = value.value.replace(KENNITALA_REGEX, '[REDACTED]')
			}
		})

		// Scrub auth headers before sending (existing hardening — preserved).
		const headers = event?.contexts?.Error?.config?.headers
		if (headers) {
			if (headers['X-Authorization-App']) headers['X-Authorization-App'] = '[REDACTED]'
			if (headers.Authorization) headers.Authorization = '[REDACTED]'
		}

		return event
	},
	integrations: [Sentry.extraErrorDataIntegration({ depth: 5 })],
})

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart
